import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import { Modal } from "@components/Modal";
import { css } from "@emotion/react";
import React from "react";

import { SHARED_PALETTE } from "@every.org/common/src/display/palette";
import {
  ContractType,
  CryptoCurrency,
} from "@every.org/common/src/entity/types/crypto";
import { SharedCryptoCurrencyConfig } from "@every.org/common/src/helpers/cryptoCurrency";

import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";
import { FontWeight, TextSize, textSizeCss } from "src/theme/text";
import { ContractTypeConfigMap } from "src/utility/cryptoCurrency";

const ContractTypeToBlockchainMap: Record<ContractType, string> = {
  [ContractType.ERC20]: "Ethereum",
  [ContractType.SPL]: "Solana",
};

// List of cryptocurrencies that should show the network confirmation modal
export const CRYPTO_CURRENCIES_WITH_NETWORK_WARNING: CryptoCurrency[] = [
  CryptoCurrency.USDC,
  CryptoCurrency.POL,
];

function getContractWarningMessage(cc: CryptoCurrency) {
  const contractType = SharedCryptoCurrencyConfig[cc].contractType;
  const abbreviation = SharedCryptoCurrencyConfig[cc].abbreviation;

  const blockchainName = contractType
    ? ContractTypeToBlockchainMap[contractType]
    : undefined;

  const tokenWarningSuffix = `Sending ${abbreviation} on other networks will result in the loss of your donation.`;

  // Special case for ETH - provide Ethereum-specific warning
  if (cc === CryptoCurrency.ETH) {
    return `Send ${abbreviation} to this address only using the Ethereum mainnet. Sending ETH on other networks will result in the loss of your donation.`;
  }

  return `Send ${abbreviation} (${contractType}) to this address using only the ${blockchainName} blockchain. ${tokenWarningSuffix}`;
}

interface NetworkConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  cryptoCurrency: CryptoCurrency;
}

export const NetworkConfirmationModal: React.FC<
  NetworkConfirmationModalProps
> = ({ isOpen, onConfirm, cryptoCurrency }) => {
  const contractType = SharedCryptoCurrencyConfig[cryptoCurrency].contractType;
  const blockchainName = contractType
    ? ContractTypeToBlockchainMap[contractType]
    : SharedCryptoCurrencyConfig[cryptoCurrency].displayName;

  return (
    <Modal
      isOpen={isOpen}
      showHeader={false}
      style={{
        overlay: {
          backgroundColor: "hsla(180, 6%, 19%, 70%)",
        },
      }}
      modalBodyCss={css`
        max-width: 500px;
        background-color: ${SHARED_PALETTE.focusComplementary};
      `}
    >
      <div
        css={[
          verticalStackCss.s,
          css`
            text-align: center;
            align-items: center;
          `,
        ]}
      >
        <Icon
          css={css`
            color: ${SHARED_PALETTE.error};
            * {
              stroke-width: 3;
            }
          `}
          iconImport={() => import("@components/Icon/icons/AlertIcon")}
          size={IconSize.X_LARGE}
          display={IconDisplay.CURRENT_COLOR}
        />
        <h3
          css={css`
            ${textSizeCss[TextSize.l]};
            font-weight: ${FontWeight.BOLD};
            margin: 0;
          `}
        >
          Important
        </h3>

        <p
          css={css`
            ${textSizeCss[TextSize.s]};
            margin: 0;
          `}
        >
          {getContractWarningMessage(cryptoCurrency)}
        </p>
        <div css={horizontalStackCss.xs}>
          <p
            css={css`
              ${textSizeCss[TextSize.s]};
              margin: 0;
              b {
                font-weight: ${FontWeight.BOLD};
              }
            `}
          >
            <b>Network:</b> {blockchainName}
          </p>
          {contractType && (
            <Icon
              iconImport={ContractTypeConfigMap[contractType].iconImport}
              size={IconSize.MEDIUM}
              display={IconDisplay.SECONDARY}
            />
          )}
        </div>

        <Button
          role={ButtonRole.PRIMARY}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: onConfirm,
          }}
          data-tname="confirmNetworkWarning"
          contentCss={css`
            width: 100%;
          `}
        >
          I understand
        </Button>
      </div>
    </Modal>
  );
};
